/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { forkJoin } from 'rxjs';

import selectStyles from '../../../util/selectStyles';
import RetailerUserModel from '../../RetailerUsers/RetailerUserModel';
import ChatbotOptionModel from '../ChatbotOptionModel';
import {
  CHATBOT_INACTIVITY_TIMEOUT_OPTIONS,
  CHATBOT_INACTIVITY_ASSIGNMENT_TYPE
} from '../../../constants/AppConstants';

const InactivityTimeoutConfig = ({
  chatbotOption,
  setChatbotOption,
  errors
}) => {
  const { id } = useParams();

  const [agentOptions, setAgentOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  useEffect(() => {
    const promises = [
      RetailerUserModel.getAgentsByRole(),
      ChatbotOptionModel.getTeamAssignments(id)
    ];

    forkJoin(promises).subscribe((results) => {
      setAgentOptions(
        results[0].agents.map((agent) => ({
          value: agent.id,
          label: `${agent.first_name} ${agent.last_name}`
        }))
      );

      setTeamOptions(
        results[1].team_assignments.map((team) => ({
          value: team[1],
          label: team[0]
        }))
      );
    });
  }, []);

  const updateField = (name, value) => {
    setChatbotOption((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (e) => {
    const enabled = e.target.checked;
    setChatbotOption((prev) => ({
      ...prev,
      inactivity_timeout_enabled: enabled,
      // Reset fields when disabling
      ...(!enabled && {
        inactivity_timeout_minutes: null,
        inactivity_assignment_type: null,
        inactivity_retailer_user_id: null,
        inactivity_team_assignment_id: null
      })
    }));
  };

  const handleSelectTime = (option) => {
    updateField('inactivity_timeout_minutes', option?.value ?? null);
  };

  const handleSelectAssignmentType = (option) => {
    setChatbotOption((prev) => ({
      ...prev,
      inactivity_assignment_type: option?.value ?? null,
      inactivity_retailer_user_id: null,
      inactivity_team_assignment_id: null
    }));
  };

  const handleSelectAgent = (option) => {
    updateField('inactivity_retailer_user_id', option?.value ?? null);
  };

  const handleSelectTeam = (option) => {
    updateField('inactivity_team_assignment_id', option?.value ?? null);
  };

  const isAssignAgent = chatbotOption.inactivity_assignment_type === 'inactivity_assign_agent';
  const isAssignTeam = chatbotOption.inactivity_assignment_type === 'inactivity_assign_team';

  return (
    <>
      <div className="col-md-12 mt-20">
        <label className="col-form-label fz-14 mr-10">
          ¿Qué pasa si el usuario no responde en cierto tiempo?
        </label>
      </div>

      <div className="col-md-12 d-flex flex-row">
        <div className="d-flex align-items-center mr-10">
          <label className="switch">
            <input
              type="checkbox"
              name="inactivity_timeout_enabled"
              checked={!!chatbotOption.inactivity_timeout_enabled}
              onChange={handleToggle}
            />
            <span className="slider round" />
          </label>
        </div>
        <div className="d-flex flex-column fz-14">
          <span className="col-form-label py-0">Asignar a un agente si no responde</span>
          <span className="text-gray-light">
            Si el usuario no responde en el tiempo indicado, se le asigna el chat a un agente y se cierra el flujo
          </span>
        </div>
      </div>

      {chatbotOption.inactivity_timeout_enabled && (
        <>
          <div className="col-md-12 mt-15">
            <div className="figma-field mb-0">
              <label>Tiempo de espera</label>
              {errors?.inactivity_timeout_minutes && (
                <span className="ml-10 error-msg fz-12">{errors.inactivity_timeout_minutes}</span>
              )}
              <Select
                options={CHATBOT_INACTIVITY_TIMEOUT_OPTIONS}
                placeholder="Selecciona un tiempo"
                className="select-tag"
                classNamePrefix="select-tag"
                styles={selectStyles}
                onChange={handleSelectTime}
                value={CHATBOT_INACTIVITY_TIMEOUT_OPTIONS.find(
                  (opt) => opt.value === chatbotOption.inactivity_timeout_minutes
                ) || null}
                components={{ IndicatorSeparator: () => null }}
              />
            </div>
          </div>

          <div className="col-md-12 mt-15">
            <div className="figma-field mb-0">
              <label>Tipo de asignación</label>
              {errors?.inactivity_assignment_type && (
                <span className="ml-10 error-msg fz-12">{errors.inactivity_assignment_type}</span>
              )}
              <Select
                options={CHATBOT_INACTIVITY_ASSIGNMENT_TYPE}
                placeholder="Selecciona el tipo de asignación"
                className="select-tag"
                classNamePrefix="select-tag"
                styles={selectStyles}
                onChange={handleSelectAssignmentType}
                value={CHATBOT_INACTIVITY_ASSIGNMENT_TYPE.find(
                  (opt) => opt.value === chatbotOption.inactivity_assignment_type
                ) || null}
                components={{ IndicatorSeparator: () => null }}
              />
            </div>
          </div>

          {isAssignAgent && (
            <div className="col-md-12 mt-15">
              <div className="figma-field mb-0">
                <label>Agente</label>
                {errors?.inactivity_retailer_user_id && (
                  <span className="ml-10 error-msg fz-12">{errors.inactivity_retailer_user_id}</span>
                )}
                <Select
                  options={agentOptions}
                  placeholder="Selecciona un agente"
                  className="select-tag"
                  classNamePrefix="select-tag"
                  styles={selectStyles}
                  onChange={handleSelectAgent}
                  value={agentOptions.find(
                    (opt) => opt.value === chatbotOption.inactivity_retailer_user_id
                  ) || null}
                  components={{ IndicatorSeparator: () => null }}
                />
              </div>
            </div>
          )}

          {isAssignTeam && (
            <div className="col-md-12 mt-15">
              <div className="figma-field mb-0">
                <label>Equipo</label>
                {errors?.inactivity_team_assignment_id && (
                  <span className="ml-10 error-msg fz-12">{errors.inactivity_team_assignment_id}</span>
                )}
                <Select
                  options={teamOptions}
                  placeholder="Selecciona un equipo"
                  className="select-tag"
                  classNamePrefix="select-tag"
                  styles={selectStyles}
                  onChange={handleSelectTeam}
                  value={teamOptions.find(
                    (opt) => opt.value === chatbotOption.inactivity_team_assignment_id
                  ) || null}
                  components={{ IndicatorSeparator: () => null }}
                />
              </div>
            </div>
          )}
        </>
      )}

      <div className="mb-20" />
    </>
  );
};

export default InactivityTimeoutConfig;
